import { useEffect, useLayoutEffect, useState } from 'react';
import { useApp } from './store/app';
import { useCoverUrl } from './store/covers';
import { accentFromImage, fallbackAccent } from './lib/color';
import { lastStarted } from './lib/bookMath';
import { useTheme } from './store/theme';
import { bootstrapSource } from './lib/sources';
import { ensureServiceWorker } from './lib/swBridge';
import { installLifecycleHooks, restoreSpeed } from './player/engine';
import { applyTheme, watchSystemTheme } from './lib/theme';
import { onRouteChange, currentRoute, screenKey } from './lib/router';
import { themeTransition } from './lib/transition';
import { Library } from './ui/Library';
import { Player } from './ui/Player';
import { Settings } from './ui/Settings';
import { MiniPlayer } from './ui/MiniPlayer';
import { ResumeDialog } from './ui/ResumeDialog';
import { IconButton } from './ui/kit';
import { CogIcon, MoonIcon, RefreshIcon, ShelfMark, SunIcon } from './ui/icons';

/** Scroll offset per screen, so coming back from a book lands you where you
 *  were in a long folder rather than at the top of it. */
const scrollMemory = new Map<string, number>();

export function App() {
  const route = useApp((s) => s.route);
  const setRoute = useApp((s) => s.setRoute);
  const syncFromRemote = useApp((s) => s.syncFromRemote);
  const themeChoice = useTheme((s) => s.choice);
  const refreshTheme = useTheme((s) => s.refresh);

  useEffect(() => {
    applyTheme(themeChoice);
  }, [themeChoice]);

  useEffect(() => watchSystemTheme(refreshTheme), [refreshTheme]);

  useEffect(() => {
    setRoute(currentRoute());
    return onRouteChange(setRoute);
  }, [setRoute]);

  useEffect(() => installLifecycleHooks(), []);
  useEffect(() => trackPointerSheen(), []);

  const key = screenKey(route);
  useLayoutEffect(() => {
    window.scrollTo(0, scrollMemory.get(key) ?? 0);
    const onScroll = () => scrollMemory.set(key, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [key]);

  useEffect(() => {
    restoreSpeed();
    // Registered up front, not just when Drive connects: a fetch-handling
    // worker is what makes the app installable as a PWA, and having it already
    // in control avoids the "reload once more" step on first Drive use.
    void ensureServiceWorker();
    // Opens your Drive folder when it is set up, and the demo library otherwise.
    void bootstrapSource();
  }, []);

  // Coming back to the tab is the natural moment to notice that another device
  // moved ahead.
  useEffect(() => {
    const onFocus = () => void syncFromRemote();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [syncFromRemote]);

  const lightUrl = useAmbientLight();

  return (
    <div className="shell">
      <div className="ambient" aria-hidden="true">
        <div className="orb orb--a" />
        <div className="orb orb--b" />
        <div className="orb orb--c" />
        <div className="orb orb--d" />
        {route.name === 'player' && lightUrl && (
          <div
            key={lightUrl}
            className="ambient__art"
            style={{ backgroundImage: `url("${lightUrl}")` }}
          />
        )}
      </div>
      {/* Keyed on the route so React remounts it and the entrance animation
       *  replays — that is what turns navigation into a transition. */}
      <div className="shell__inner route" key={key}>
        {route.name === 'library' && (
          <>
            <TopBar />
            <Library />
          </>
        )}
        {route.name === 'player' && <Player bookId={route.bookId} />}
        {route.name === 'settings' && <Settings />}
      </div>
      <MiniPlayer />
      <ResumeDialog />
    </div>
  );
}


/**
 * Lights the room with the book in hand — the one open in the player, else the
 * one loaded, else the one you were last part-way through — by setting the cover's colour as --book-h/s/l on <html>.
 * Those are registered properties, so the light eases from book to book.
 * Returns the cover URL, which the player also blurs into the background.
 */
function useAmbientLight(): string | undefined {
  const book = useApp((s) => {
    const id =
      s.route.name === 'player' ? s.route.bookId
      : (s.currentBookId ?? lastStarted(s.books, s.progress.books)?.book.id);
    return id ? (s.books.find((b) => b.id === id) ?? null) : null;
  });
  const cover = useCoverUrl(book);
  const title = book?.title;

  useEffect(() => {
    const style = document.documentElement.style;
    const apply = (hsl: string | null) => {
      const [h, s, l] = hsl?.split(' ') ?? [];
      if (!h || !s || !l) {
        for (const p of ['--book-h', '--book-s', '--book-l']) style.removeProperty(p);
        return;
      }
      style.setProperty('--book-h', h);
      style.setProperty('--book-s', s);
      style.setProperty('--book-l', l);
    };

    if (!title) return apply(null);
    if (!cover) return apply(fallbackAccent(title));
    let cancelled = false;
    void accentFromImage(cover, title).then((hsl) => {
      if (!cancelled) apply(hsl);
    });
    return () => {
      cancelled = true;
    };
  }, [cover, title]);

  return cover;
}

/**
 * Feeds the pointer position into whichever glass pane it is over (--mx/--my),
 * which its ::after sheen follows. Mouse only; one listener for the page,
 * coalesced to a frame.
 */
function trackPointerSheen(): () => void {
  let frame = 0;
  let last: PointerEvent | null = null;

  const onMove = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    last = event;
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const e = last;
      const pane = (e?.target as Element | null)?.closest?.<HTMLElement>('.glass, .tile');
      if (!e || !pane) return;
      const r = pane.getBoundingClientRect();
      pane.style.setProperty('--mx', `${e.clientX - r.left}px`);
      pane.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
  };

  window.addEventListener('pointermove', onMove, { passive: true });
  return () => {
    window.removeEventListener('pointermove', onMove);
    cancelAnimationFrame(frame);
  };
}

/** True once the page has scrolled, so the bar firms up over the content. */
function useScrolled(): boolean {
  const [scrolled, setScrolled] = useState(() => window.scrollY > 8);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

function TopBar() {
  const navigate = useApp((s) => s.navigate);
  const loadLibrary = useApp((s) => s.loadLibrary);
  const label = useApp((s) => s.source?.label ?? '');
  const libraryStatus = useApp((s) => s.libraryStatus);
  const resolved = useTheme((s) => s.resolved);
  const setChoice = useTheme((s) => s.setChoice);
  const scrolled = useScrolled();

  const toggleTheme = (event: React.MouseEvent<HTMLButtonElement>) => {
    const next = resolved === 'dark' ? 'light' : 'dark';
    // From the pointer, or from the button's centre for a key press.
    const box = event.currentTarget.getBoundingClientRect();
    const x = event.clientX || box.left + box.width / 2;
    const y = event.clientY || box.top + box.height / 2;
    themeTransition(() => setChoice(next), x, y);
  };

  return (
    <header className="glass topbar" data-scrolled={scrolled || undefined}>
      <div className="topbar__brand">
        <ShelfMark className="topbar__mark" />
        <div className="topbar__names">
          <h1 className="topbar__title">Shelf</h1>
          {label && <p className="topbar__sub">{label}</p>}
        </div>
      </div>

      <div className="topbar__actions" role="toolbar" aria-label="Library">
        <IconButton
          bare
          size={42}
          label="Refresh library"
          onClick={() => void loadLibrary({ force: true })}
          disabled={libraryStatus === 'loading'}
        >
          <RefreshIcon size={19} />
        </IconButton>

        <IconButton
          bare
          size={42}
          label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggleTheme}
        >
          {resolved === 'dark' ?
            <SunIcon size={19} />
          : <MoonIcon size={19} />}
        </IconButton>

        <IconButton bare size={42} label="Settings" onClick={() => navigate({ name: 'settings' })}>
          <CogIcon size={19} />
        </IconButton>
      </div>
    </header>
  );
}
